import { useEffect, useRef } from 'react'
import { toast } from 'sonner'

import type { Expense } from '@/lib/api/expense-api'
import { useExpense } from '@/hooks/use-expense-query'
import { m } from '@/lib/paraglide/messages'

const UUID_RE =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** `?expense=` trên URL → id khoản chi hợp lệ, giá trị lạ thì bỏ. */
export function pickExpenseId(raw: unknown): string | undefined {
    return typeof raw === 'string' && UUID_RE.test(raw.trim())
        ? raw.trim().toLowerCase()
        : undefined
}

interface Options {
    /** Id khoản chi trên URL (`/accounting/expenses?expense=<id>`). */
    expenseId?: string
    /** Người xem mở được popup chi tiết (popup sửa cần `expenses.write`). */
    enabled: boolean
    /** Popup chi tiết đang mở — đóng popup đã mở từ link thì xoá `?expense=`. */
    open: boolean
    onOpen: (expense: Expense) => void
    /** Bỏ `?expense=` khỏi URL. */
    onClear: () => void
}

/**
 * Mở thẳng popup chi tiết một khoản chi từ link (mã phiếu chi ở Báo cáo thu
 * chi, M-143): tải khoản chi theo id rồi mở popup đúng một lần cho mỗi id;
 * không tìm thấy (đã xoá / sai id) thì báo và bỏ tham số; đóng popup thì bỏ
 * tham số để tải lại trang không bật lại popup.
 */
export function useExpenseDeepLink({
    expenseId,
    enabled,
    open,
    onOpen,
    onClear,
}: Options) {
    const active = enabled && !!expenseId
    const { data, isError, isSuccess } = useExpense(active ? expenseId : '')
    // Callback mới mỗi render: giữ bản mới nhất trong ref để effect không chạy
    // lại theo nó.
    const handlers = useRef({ onOpen, onClear })
    useEffect(() => {
        handlers.current = { onOpen, onClear }
    })

    const handled = useRef<string | null>(null)
    const openedFromLink = useRef(false)

    useEffect(() => {
        if (!active || handled.current === expenseId) return
        if (data) {
            handled.current = expenseId
            openedFromLink.current = true
            handlers.current.onOpen(data)
        } else if (isError || isSuccess) {
            handled.current = expenseId
            toast.error(m.expense_deep_link_not_found())
            handlers.current.onClear()
        }
    }, [active, expenseId, data, isError, isSuccess])

    // Chỉ xoá tham số khi popup mở từ link chuyển sang đóng.
    const wasOpen = useRef(open)
    useEffect(() => {
        if (wasOpen.current && !open && openedFromLink.current) {
            openedFromLink.current = false
            handlers.current.onClear()
        }
        wasOpen.current = open
    }, [open])
}
